import React from "react"
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from "@mui/material"
import { Assignment, TrendingUp, CheckCircle } from "@mui/icons-material"
import type { AuditProduct } from "../types"

interface AuditConfirmationProps {
  product: AuditProduct
  onContinue: () => void
  onComplete: () => void
}

const AuditConfirmation: React.FC<AuditConfirmationProps> = ({ product, onContinue, onComplete }) => {
  return (
    <Box>
      <Box textAlign="center" mb={4}>
        <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Audit Submitted Successfully!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          The audit for <strong>{product.name}</strong> has been completed and submitted
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            What happens next?
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <Assignment color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Lots Marked in Audit Table"
                secondary="Each scanned lot has been marked as Excess, Short, or Matched in the audit database"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <TrendingUp color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Reports Generated"
                secondary="Audit reports and discrepancy alerts have been generated for management review"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Audit Complete!</strong> The product status has been updated to "COMPLETED" and all discrepancies have
          been recorded for further action.
        </Typography>
      </Alert>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="center" gap={2}>
        <Button variant="outlined" onClick={onComplete} size="large">
          Go to listing
        </Button>
        <Button variant="contained" onClick={onContinue} size="large">
          Continue with Next Product
        </Button>
      </Box>

      <Box textAlign="center" mt={2}>
        <Typography variant="body2" color="text.secondary">
          You can continue auditing other products or finish this session
        </Typography>
      </Box>
    </Box>
  )
}

export default AuditConfirmation
